<template>
  <div v-if="data" class="article-content flex-1" :class="{'pt-12': !faqMode, 'faq-content': faqMode}">
    <div
      :class="{'nv-mx-auto': data && !data.toc.length, 'lg:w-11/12 2xl:w-10/12': !faqMode}"
      class="block mx-auto"
    >
      <h1 v-if="title" class="mb-10">{{ title }}</h1>

      <div class="block mb-6 text-gray-500" v-if="createdAt">
        <span class="flex items-center gap-2" :title="$t('updated_date')">
          <nv-icon>
            history
          </nv-icon>
          {{ $formatDate(createdAt) }}
        </span>
      </div>

      <article v-html="data.body" />
    </div>
  </div>
</template>
<script lang="ts" setup>
// Packages
import * as markdown from 'markdown-it';
import { parse } from 'node-html-parser';
import slugify from 'slugify';

const doc = markdown.default();

// Remember the old renderer if overridden, or proxy to the default renderer.
const defaultRender = doc.renderer.rules.link_open || function (tokens, idx, options, env, self) {
  return self.renderToken(tokens, idx, options);
};


doc.renderer.rules.link_open = function (tokens, idx, options, env, self) {
  // Add a new `target` attribute, or replace the value of the existing one.
  tokens[idx].attrSet('target', '_blank');

  // Pass the token to the default renderer.
  return defaultRender(tokens, idx, options, env, self);
};

// ---------------------------------------------
// Props
// ---------------------------------------------

const props = defineProps({
  content: {
    type: String,
    required: true,
  },
  title: {
    type: String,
    required: false
  },
  showNav: {
    type: Boolean,
    required: false,
    default: false
  },
  createdAt: {
    type: String,
    required: false
  },
  faqMode: {
    type: Boolean,
    required: false
  }
});

// ---------------------------------------------
// State
// ---------------------------------------------
const data = ref<{
  body: string,
  toc: Array<{ text: string; path: string; id: string; }>
}>(renderMarkdown());



// ---------------------------------------------
// Methods
// ---------------------------------------------

function renderMarkdown() {
  const html = doc.render(props.content);

  const root = parse(html);
  const headers = root.querySelectorAll('h1, h2, h3, h4, h5, h6');

  const toc = [];

  for (const h of headers) {
    const slug = h.getAttribute('id') || slugify(h.textContent);
    h.setAttribute('id', slug);
    h.innerHTML = `<a href="#${slug}">${h.innerHTML}</a>`;

    toc.push({ text: h.textContent, path: `${slug}`, id: slug });
  }

  return { body: root.toString(), toc };
}
</script>
<style lang="scss">
.article-content {
  nav {
    ul {
      list-style: none;

      li {
        margin-bottom: 8px;
        padding: 8px;
      }
    }
  }

  p {
    margin: 0 0 1.4em;
  }

  ul {
    padding-left: 1.4rem;
    margin: 2rem 0;
    list-style: disc;
  }

  ol {
    padding-right: 16px;
  }

  h3 ~ p {
    margin-bottom: 32px;
  }

  p {
    img {
      display: block;
      margin: 32px 0;
      max-width: 100%;
    }
  }
}
</style>
